import React, { Suspense, lazy, useEffect } from "react";

import Helmet from "react-helmet";

import { removeIFoodWidget } from "./utils/window";

// REDUX
import { useDispatch, useSelector } from "react-redux";

// ROUTES
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";

// SCREENS
import Navbar from "./components/navbar";

import NewOrder from "./components/orders/newOrder";

import DeliveryCenterCreateEdit from "./components/deliveryCenter";

import OrdersReports from "./components/orders";

import Products from "./components/itemMenu";

import Customers from "./components/customers";

import MerchantCreateOrEdit from "./components/restaurants/merchantCreateOrEdit";

import Cashiers from "./components/cashier";
import FiscalTickets from "./components/fiscal";

import Login from "./components/user/login";
import Signup from "./components/user/signup";
import ChangePassword from "./components/user/changePassword";
import ForgotPassword from "./components/user/ForgotPassword";
import ResetPassword from "./components/user/ResetPassword";

import ReportIndex from "./components/report";

import DeliveriesHistory from "./components/history/deliveries";
// MANAGERS
import AlertManager from "./components/managers/AlertManager";
import OrderManager from "./components/managers/OrderManager";
import DriverManager from "./components/managers/DriverManager";
import CashierManager from "./components/cashier/CashierManager";
import SnackbarManager from "./components/snackbar";
import ShippingRouteManager from "./components/managers/ShippingRouteManager";
import OperationDataManager from "./components/managers/OperationDataManager";
import PrintersManager from "./components/managers/PrintersManager";
import SubscriptionExpiryManager from "./components/managers/SubscriptionExpiryManager";
import { cleanCancelledOrderCache } from "./store/actions/ordersSlice";
import NotFoundPage from "./components/NotFound/NotFoundPage";
import CostsIndexPage from "./components/CostControl";
import Dashboard from "./components/dashboard/Dashboard";
import SimpleLabel from "./screens/SimpleLabel";
import SimpleStock from "./screens/SimpleStock";
import SimpleEmployee from "./screens/SimpleEmployee";
import SlapieSimples from "./screens/SlapieSimples";
import PrintersHelpScreen from "./screens/Help/PrintersHelpScreen";
import LeadForm from "./screens/LeadForm";
import MySubscriptionPage from "./components/subscription/MySubscriptionPage";
import CheckoutSuccess from "./screens/Subscription/CheckoutSuccess";
import Terms from "./screens/Legal/Terms";
import Privacy from "./screens/Legal/Privacy";

// Code-split: the assistant pulls in react-markdown + recharts, which would
// otherwise land in the main bundle for every page load.
const AssistantScreen = lazy(() => import("./screens/Assistant"));
// Code-split: @google/genai is only needed by the kitchen's voice chef.
const KitchenScreen = lazy(() => import("./screens/Kitchen"));

const Routes = () => {
  const dispatch = useDispatch();

  const user = useSelector((state) => state.auth.user);
  const pageTitle = useSelector((state) => state.pageTitle.title || "");

  const isFullScreen = useSelector((state) => state.dashboard.isFullScreen);

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(cleanCancelledOrderCache());
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [dispatch]);

  useEffect(() => {
    if (!pageTitle.startsWith("Central")) {
      removeIFoodWidget();
    }
  }, [pageTitle]);

  return (
    <BrowserRouter>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Switch>
        <Redirect exact from="/" to="/central-delivery" />
        <Redirect exact from="/dashboard" to="/central-delivery" />

        <Route exact path="/signup" component={Signup} />
        <Route exact path="/login" component={Login} />
        <Route exact path="/forgot-password" component={ForgotPassword} />
        <Route exact path="/password-reset/confirm" component={ResetPassword} />
        <Route exact path="/interesse" component={LeadForm} />
        <Route exact path="/termos" component={Terms} />
        <Route exact path="/politica-privacidade" component={Privacy} />

        <ProtectedRoute
          exact
          path="/change-password"
          component={ChangePassword}
        />

        <ProtectedRoute
          exact
          path="/central-delivery"
          render={(props) => <Dashboard {...props} mode="orders" />}
        />
        <ProtectedRoute
          exact
          path="/central-salao"
          render={(props) => <Dashboard {...props} mode="saloon" />}
        />
        <ProtectedRoute
          exact
          path="/central-embalagem"
          render={(props) => <Dashboard {...props} mode="packing" />}
        />
        <ProtectedRoute exact path="/pedido/novo" component={NewOrder} />

        <ProtectedRoute
          exact
          path="/delivery-center"
          component={DeliveryCenterCreateEdit}
        />

        <ProtectedRoute
          exact
          path="/controle-custos"
          component={CostsIndexPage}
        />

        <ProtectedRoute
          exact
          path="/etiqueta-simples"
          component={SimpleLabel}
        />

        <ProtectedRoute
          exact
          path="/estoque-simples"
          component={SimpleStock}
        />


        <ProtectedRoute
          exact
          path="/funcionario-simples"
          component={SimpleEmployee}
        />

        <ProtectedRoute
          exact
          path="/slapie-simples"
          component={SlapieSimples}
        />

        <ProtectedRoute
          exact
          path="/assistente-ia"
          render={(props) => (
            <Suspense fallback={<div />}>
              <AssistantScreen {...props} />
            </Suspense>
          )}
        />

        <ProtectedRoute
          exact
          path="/cozinha"
          render={(props) => (
            <Suspense fallback={<div />}>
              <KitchenScreen {...props} />
            </Suspense>
          )}
        />

        <ProtectedRoute
          exact
          path="/historico/fiscal"
          component={FiscalTickets}
        />

        <ProtectedRoute path="/historico/pedidos" component={OrdersReports} />
        <ProtectedRoute
          path="/historico/entregas"
          component={DeliveriesHistory}
        />
        <ProtectedRoute exact path="/historico/caixa" component={Cashiers} />

        <ProtectedRoute path="/reports" component={ReportIndex} />

        <ProtectedRoute path="/products" component={Products} />

        <ProtectedRoute path="/customers" component={Customers} />

        <ProtectedRoute exact path="/minha-assinatura" component={MySubscriptionPage} />
        <ProtectedRoute exact path="/assinatura/sucesso" component={CheckoutSuccess} />

        <ProtectedRoute path="/ajuda" component={PrintersHelpScreen} />

        <ProtectedRoute
          path="/restaurant/new"
          component={MerchantCreateOrEdit}
        />
        <ProtectedRoute
          path="/restaurant/:id"
          component={MerchantCreateOrEdit}
        />

        <Route component={NotFoundPage} />
      </Switch>

      {user && (
        <>
          {/*<DashboardTour />*/}
          <DriverManager />
          <OrderManager />
          <OperationDataManager />
          <PrintersManager />
          <ShippingRouteManager />
          <CashierManager />
          <AlertManager />
          <SnackbarManager />
          <SubscriptionExpiryManager />

          {isFullScreen === false && <Navbar />}
        </>
      )}
    </BrowserRouter>
  );
};

export default Routes;
